import { h, type VNode } from 'vue';
import { message } from 'ant-design-vue';
import i18n from '@/i18n';

import Utils from '@/api/Utils';
import { isRecord } from '@/utils/typeGuards';


const t = i18n.global.t;


type UniquenessSource = { object_type: string; field?: string } & Record<string, unknown>;

type UniquenessError = { message_id: string; sources: UniquenessSource[] } & Record<string, unknown>;

type JsonParseResult = [unknown, true] | [string, false];


const isUniquenessSource = (value: unknown): value is UniquenessSource => {
	if (!isRecord(value)) {
		return false;
	}
	return typeof value.object_type === 'string';
};


const isUniquenessError = (data: unknown): data is UniquenessError => {
	if (!isRecord(data)) {
		return false;
	}
	if (typeof data.message_id !== 'string') {
		return false;
	}
	if (!Array.isArray(data.sources)) {
		return false;
	}
	return data.sources.every(isUniquenessSource);
};


const getJsonData = (text: string): JsonParseResult => {
	try {
		return [JSON.parse(text), true];
	} catch {
		return [text, false];
	}
};


const getErrorText = (key: string, value: string): string => {
	const [data, isJson] = getJsonData(value);
	if (!isJson || !isUniquenessError(data)) {
		return value;
	}
	const lines = [t('uniqueness.messages.' + data.message_id, data)];
	for (const source of data.sources) {
		const parts = ['uniqueness.fields', source.object_type];
		if (source.field) {
			parts.push(source.field);
		}
		lines.push(t(parts.join('.'), source));
	}
	return lines.join('\n');
};


const getErrorComponent = (key: string, value: string): VNode => {
	const [data, isJson] = getJsonData(value);
	if (!isJson || !isUniquenessError(data)) {
		const header = key ? (key + ': ') : '';
		return h('div', header + value);
	}
	const services = data.sources.map(
		source => {
			const parts = ['uniqueness.fields', source.object_type];
			if (source.field) {
				parts.push(source.field);
			}
			return h('li', t(parts.join('.'), source));
		},
	);
	const contents: VNode[] = [h('div', t('uniqueness.messages.' + data.message_id, data))];
	if (services.length > 0) {
		contents.push(h('ul', { style: { 'padding-inline-start': '16px' } }, services));
	}
	return h('div', contents);
};


const getErrorComponents = (errors: unknown): VNode[] => {
	const components: VNode[] = [];
	if (Utils.isObjectWithData(errors) && isRecord(errors)) {
		for (const [key, values] of Object.entries(errors)) {
			if (Array.isArray(values)) {
				for (const value of values) {
					if (key === 'non_field_errors') {
						components.push(getErrorComponent('', String(value)));
					} else {
						components.push(getErrorComponent(key, String(value)));
					}
				}
			} else if (Utils.isObjectWithData(values) && isRecord(values)) {
				for (const [subKey, subValue] of Object.entries(values)) {
					const fullKey = key + '.' + subKey;
					if (Array.isArray(subValue)) {
						for (const v of subValue) {
							components.push(getErrorComponent(fullKey, String(v)));
						}
					} else {
						components.push(getErrorComponent(fullKey, String(subValue)));
					}
				}
			} else {
				const value = String(values);
				if (key === 'non_field_errors') {
					components.push(getErrorComponent('', value));
				} else {
					components.push(getErrorComponent(key, value));
				}
			}
		}
	}
	return components;
};


const showErrorMessage = (errors: unknown): void => {
	const components = getErrorComponents(errors);
	message.error(h('span', [
		h('span', { style: { 'font-weight': 'bold' } }, t('common.error')),
		h('div', { style: { 'text-align': 'left' } }, components),
	]));
};


export {
	getErrorText,
	getErrorComponents,
	showErrorMessage,
};
